import { names } from '../copy/names';
import { shell } from '../copy/shell';

/**
 * Mirrors the API's own name rule so a bad name is refused before a round trip:
 * 1-128 characters, no C0/C1 control characters, and not only whitespace.
 *
 * Length counts Unicode code points, matching Rust's `chars().count()`. `"x".length`
 * would count UTF-16 units and disagree on anything outside the BMP.
 */
export const NAME_MAX = 128;

const CONTROL_RE = /[\u0000-\u001F\u007F-\u009F]/;

export type NameProblem = 'empty' | 'blank' | 'too-long' | 'control-char';

export function validateName(raw: string): NameProblem | null {
  if (raw.length === 0) return 'empty';
  if (raw.trim().length === 0) return 'blank';
  if ([...raw].length > NAME_MAX) return 'too-long';
  if (CONTROL_RE.test(raw)) return 'control-char';
  return null;
}

/** Each problem's line, from src/copy/names.ts. */
export const NAME_PROBLEM_COPY: Record<NameProblem, string> = {
  empty: names.empty,
  blank: names.blank,
  'too-long': names.tooLong(NAME_MAX),
  'control-char': names.controlChar,
};

/**
 * What to call the tenant in the UI.
 *
 * A tenant created at first login is named after whichever identifier the access token
 * carried — the email, or the Auth0 subject when there was no email, which with a default
 * Auth0 setup is what happens. Printing `auth0|64f2…` in the nav footer as though the user
 * had chosen it is noise, so when the name is just the account's own identifier we say
 * "Your tenant" instead. A name the user would recognise is always shown as-is.
 */
export function tenantDisplay(
  tenantName: string,
  idToken: { email?: string | undefined; sub?: string | undefined } | undefined,
  fallback: string = shell.yourTenant,
): string {
  if (!idToken) return tenantName;
  const autoNamed = tenantName === idToken.email || tenantName === idToken.sub;
  return autoNamed ? fallback : tenantName;
}

/** Whether the tenant name is just the account's own identifier. Settings explains it. */
export function isAutoNamedTenant(
  tenantName: string,
  idToken: { email?: string | undefined; sub?: string | undefined } | undefined,
): boolean {
  return Boolean(idToken) && (tenantName === idToken?.email || tenantName === idToken?.sub);
}
